import { FC, useEffect, useRef, useState } from 'react';
import { CareMapDrawerPage, IDrawerModalAnimationOrigin } from '../components/CareMapDrawerPage';
import { IContactMethod, IRelationship } from '../caremap/ICareMap';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { TextField } from '../components/TextField';
import { IonButton } from '@ionic/react';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CareMapMemberAvatar } from '../avatar/Avatars';
import { AvatarIdentityFactory } from '../avatar/AvatarIdentityFactory';
import { CareMapDrawerState } from '../caremap/CareMapDrawerState';
import { RelationshipSelectField } from '../components/RelationshipSelectField';
import { useDeleteHouseholdMember } from './useDeleteHouseholdMember';
import { CareMapFactory } from '../caremap/CareMapFactory';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { DeleteMemberButton } from '../support-group/DeleteMemberButton';
import { CustomTypeAddedMetaBuilder } from '../metrics/CustomTypeAddedMetaBuilder';
import { EditHouseholdMemberAvatarDrawer } from '../avatar/EditAvatarDrawer';
import { DefaultAvatarProvider } from '../avatar/DefaultAvatarProvider';
import { MemberFormState } from '../avatar/MemberFormState';
import { CaregiverToggle } from './CaregiverToggle';
import { IPhoneContactMethod, PhoneField } from '../contact-methods/PhoneField';
import { EmailField, IEmailContactMethod } from '../contact-methods/EmailField';
import { IAddFromContactsButtonResult, AddFromContactsButton } from '../contacts/AddFromContactsButton';
import { LinkedMemberEmailField } from '../caremap/LinkedMemberEmailField';
import { useConfirmLinkThenSave } from '../caremap/useConfirmLinkThenSave';


export interface IHouseholdMemberEditProps {
    memberId?: string;
    autoSelectFromContacts?: boolean;
};

export const HouseholdMemberEdit: FC<IHouseholdMemberEditProps> = ({ memberId, autoSelectFromContacts }) => {
    const tracking = useInstance(MixpanelService);

    const history = useInstance(HistoryViewModel);

    const drawerState = useInstance(CareMapDrawerState);

    const careMapFactory = useInstance(CareMapFactory);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const memberForm = useInstance(MemberFormState);
    useSubscription(() => memberForm);

    const [addedFromContacts, setAddedFromContacts] = useState<boolean>(false);

    const defaultAvatarProvider = useInstance(DefaultAvatarProvider);
    const avatarIdentities = useInstance(AvatarIdentityFactory);

    const confirmLinkThenSave = useConfirmLinkThenSave();

    const familyOnboardingComplete = careMapState.hasCompletedOnboardingStep('household-members');

    const getFirstUnusedAvatar = () => {
        return defaultAvatarProvider.getDefaultAvatar({ careMap: careMapState });
    };

    useEffect(() => {
        drawerState.setIsMaskingFocusedCareMap(true);

        return () => {
            drawerState.setIsMaskingFocusedCareMap(false);
        };
    }, [drawerState]);

    useEffect(() => {
        if (!memberId){
            memberForm.init({
                id: careMapFactory.getNodeId(),
                firstName: '',
                relationshipId: undefined,
                relationship: undefined,
                contactMethods: [],
                avatar: getFirstUnusedAvatar(),
            });
        } else {
            const initialMember = careMapState.maybeGetHouseholdMember(memberId);
            if (initialMember){
                memberForm.init(initialMember);
            }
            // otherwise, 404 handling (skipping for now)
        }

        const idToClear = memberForm.state.member?.id;

        return () => {
            // protect against edge case that this form is animating out (and so hasn't cleared yet)
            // while another form is animating in, triggering init
            // but then this form clear's that form's state
            if (idToClear && memberForm.state.member?.id === idToClear){
                memberForm.clear();
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberId, memberForm, careMapState, careMapFactory]);

    const addFromContactsRef = useRef<HTMLIonButtonElement>(null);
    useEffect(() => {
        if(autoSelectFromContacts && addFromContactsRef.current) {
            addFromContactsRef.current.click();
        }
    }, [addFromContactsRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const isAdding = (!memberId);

    const onDelete = useDeleteHouseholdMember({ memberId: memberId || '', onDelete: () => {
        // need to go back twice to skip through read-only page
        // history.back({ pages: 2 });
        // TODO: why isn't back({ pages: 2 }) working
        history.push(`/caremaps/${careMapState.getActiveCaremap()?.careMapId}/drawer/household`);
    }});

    const [showingAvatarPicker, setIsShowingAvatarPicker] = useState(false);
    const [avatarDrawerAnimationOrigin, setAvatarDrawerAnimationOrigin] = useState<IDrawerModalAnimationOrigin>();
    const avatarClicked = (e: React.MouseEvent) => {
        setAvatarDrawerAnimationOrigin({x: e.clientX, y: e.clientY});
        setIsShowingAvatarPicker(true);
    };

    const [isValid, setIsValid] = useState<boolean>(false);
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
    const requireExtraFields = !familyOnboardingComplete;// && isAdding;

    const member = memberForm.state.member;

    useEffect(() => {
        if (member) {
            // A linked member's email is their account email and is always valid;
            // only the editable EmailField reports validity, so don't block Save for linked members.
            const emailOk = member.linkedUserId ? true : isEmailValid;
            setIsValid(
                requireExtraFields ?
                    Boolean(
                        member.firstName?.trim() &&
                        member.lastName?.trim() &&
                        member.relationship &&
                        emailOk
                    ) :
                    Boolean(member.firstName?.trim() || member.relationship)
            );
        }
    }, [requireExtraFields, isEmailValid, member?.linkedUserId, member]);

    if (!member){
        // either haven't hit the useEffect above that inits it
        // or the member didn't exist
        return null;
    }

    const isMyMember = careMapState.isMyMember(member);

    const onCancel = () => {
        history.back();
    };

    const actuallySave = () => {
        if (isAdding) {
            careMapState.addHouseholdMembers([member]);
        } else {
            careMapState.updateHouseholdMember(member);
        }

        tracking.track(`Household Member ${isAdding ? 'Added' : 'Edited'}`, () => ({
            'Added From Contacts': addedFromContacts,
            'primary caregiver': Boolean(member.primaryCaregiver),
            '# of Contact Methods': careMapState.cleanupContactMethods(member.contactMethods).length
        }));

        history.back();
    };

    const onSave = () => {
        confirmLinkThenSave({
            linkedUserId: member.linkedUserId,
            email: careMapState.getContactMethodOrDefault(member, 'email').value,
            name: member.firstName,
            isSelf: isMyMember,
            memberNoun: 'inner circle member',
            save: actuallySave
        });
    };

    // Unlink: clear the email contact method and persist via the normal save path so the server
    // drops linkedUserId. Also clear linkedUserId optimistically so the form immediately flips
    // back to the editable EmailField rather than waiting for the post-save refresh.
    const onUnlink = () => {
        const contactMethods = member.contactMethods.filter(m => m.type !== 'email');
        memberForm.patch({ contactMethods, linkedUserId: undefined });
        careMapState.updateHouseholdMember({ ...member, contactMethods, linkedUserId: undefined });
    };

    const relationshipChangedMetrics = ({ relationshipId, relationship }: { relationshipId?: IRelationship; relationship?: string; }) => {
        if (relationship && !relationshipId){
            tracking.trackWithDebounce({
                event: 'Custom Type Added',
                getContext: () => new CustomTypeAddedMetaBuilder()
                    .origin('caremap')
                    .household()
                    .field({
                        field: 'relationship',
                        fieldLabelKey: 'HOUSEHOLD_MEMBER__RELATIONSHIP',
                        value: relationship
                    })
                    .build()

            });
        }
    };

    const relationshipChanged = ({ relationshipId, relationship }: { relationshipId?: IRelationship; relationship?: string; }) => {
        relationshipChangedMetrics({ relationship, relationshipId });

        memberForm.patch({ relationshipId, relationship });
    };

    const togglePrimaryCaregiver = (checked: boolean) => {
        memberForm.patch({ primaryCaregiver: checked });
    };

    const contactMethodChanged = (method: IContactMethod) => {
        const copy = [ ...member.contactMethods ];
        const idx = copy.findIndex(m => m.type === method.type);

        idx > -1 ? copy.splice(idx, 1, method) : copy.push(method);

        memberForm.patch({ contactMethods: copy });
    };

    const title = isMyMember
        ? 'Edit Your Info'
        : `${isAdding ? 'Add' : 'Edit'} Inner Circle Member`;

    const buttons = <>
        <IonButton data-type="save-button" disabled={!isValid} onClick={onSave}>Save</IonButton>
        <IonButton data-type="cancel-button" fill="outline" onClick={onCancel}>Cancel</IonButton>
    </>;

    return (
        <>
            <CareMapDrawerPage
                title={title}
                hasBack={true}
                buttons={buttons}
                data-type="member-edit"
                data-member-id={memberId}
            >
                {!careMapState.isMyMember(member) && (
                    <AddFromContactsButton
                        btnRef={addFromContactsRef}
                        onContactSelected={((patch: IAddFromContactsButtonResult) => {
                            delete patch.organization;
                            setAddedFromContacts(true);
                            memberForm.patch({...member, ...patch});
                        })}
                    />
                )}

                <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>

                    <div style={{display: 'flex', alignItems: 'start', width: '100%', gap: 20}}>
                        <CareMapMemberAvatar
                            identity={avatarIdentities.forMember(member)}
                            showsStoredAvatar
                            onClick={(e: React.MouseEvent) => avatarClicked(e)}
                        />

                        <div style={{display: 'flex', flexDirection: 'column', gap: 20, flexGrow: 1}}>
                            <TextField
                                name="first-name"
                                label="First Name"
                                value={member.firstName}
                                autocapitalize="words"
                                required={requireExtraFields}
                                onInput={firstName => memberForm.patch({ firstName })}
                            />
                            <TextField
                                name="last-name"
                                label="Last Name"
                                value={member.lastName}
                                autocapitalize="words"
                                required={requireExtraFields}
                                onInput={lastName => memberForm.patch({ lastName })}
                            />
                        </div>
                    </div>

                    <RelationshipSelectField
                        key={member.id}
                        required={requireExtraFields}
                        label={careMapState.getLabel('HOUSEHOLD_MEMBER__RELATIONSHIP')}
                        relationshipId={member.relationshipId}
                        relationship={member.relationship}
                        onChange={relationshipChanged}
                    />

                    <TextField
                        name="address"
                        label="Address"
                        value={member.address}
                        autocapitalize="words"
                        onInput={address => memberForm.patch({ address })}
                    />

                    <div style={{marginTop: 8}}>
                        <CaregiverToggle
                            checked={member.primaryCaregiver}
                            onToggle={togglePrimaryCaregiver}
                        />
                    </div>

                    {member.linkedUserId ? (
                        <LinkedMemberEmailField
                            email={careMapState.getContactMethodOrDefault(member, 'email').value}
                            linkedToName={member.firstName || 'this person'}
                            onUnlink={onUnlink}
                            isSelf={isMyMember}
                        />
                    ) : (
                        <EmailField
                            required={requireExtraFields}
                            methodChanged={contactMethodChanged}
                            isValid={valid => setIsEmailValid(valid)}
                            method={careMapState.getContactMethodOrDefault(member, 'email') as IEmailContactMethod}
                        />
                    )}

                    <PhoneField
                        methodChanged={contactMethodChanged}
                        method={careMapState.getContactMethodOrDefault(member, 'mobile-phone', ) as IPhoneContactMethod}
                    />
                    <PhoneField
                        methodChanged={contactMethodChanged}
                        method={careMapState.getContactMethodOrDefault(member, 'work-phone', ) as IPhoneContactMethod}
                    />
                </div>

                {!isAdding &&
                    <DeleteMemberButton onDelete={onDelete} memberLabel="Inner Circle Member" />
                }
            </CareMapDrawerPage>
            <EditHouseholdMemberAvatarDrawer
                isOpen={showingAvatarPicker}
                animationOrigin={avatarDrawerAnimationOrigin}
                member={member}
                onSave={changed => {
                    memberForm.patch(changed);
                    setIsShowingAvatarPicker(false);
                }}
                onCancel={() => setIsShowingAvatarPicker(false)}
            />
        </>

    );
};
